import { lazy, useEffect, useState } from 'react';
import { trackEvent } from 'utils/tracking';
import { useStore } from 'zustand';
import { ModelType } from '../../../types';
import { fetchWithType } from '../../../utils/api';
import {
  getAdminUrl,
  getApiToken,
  getUniqId,
  locationIsKia,
} from '../../../utils/helpers';
import { AppProps } from '../../App';
import CarouselDirection from '../../components/carousel/CarouselDirection';
import CarouselFilters from '../../components/carousel/CarouselFilters';
import CarouselMileageFilter from '../../components/carousel/CarouselMileageFilter';
import CarouselSlider from '../../components/carousel/CarouselSlider';
import { getStoreByKey } from '../../components/carousel/store/CarouselStore';
import {
  CarouselFieldsEnum,
  SortableCarouselField,
} from '../../components/carousel/types/CarouselTypes';
import SecondaryButton from '../../components/ui/SecondaryButton';

const Snippet = lazy(() => import('../../components/Snippet'));
const CarouselControls = lazy(
  () => import('../../components/carousel/CarouselControls'),
);

const maybeUniqId = getUniqId();

const Carousel = ({
  pricelist,
  fields,
  heading,
  imageType,
  fuelId,
  categoryIds,
  hideControls,
  uniqKey,
  sort,
  sortBy,
  privateMileages,
  onlyBusinessEdition,
  includeBusinessEdition,
}: Pick<
  AppProps,
  | 'pricelist'
  | 'fields'
  | 'heading'
  | 'imageType'
  | 'fuelId'
  | 'categoryIds'
  | 'privateMileages'
  | 'onlyBusinessEdition'
  | 'includeBusinessEdition'
> & {
  hideControls?: boolean;
  sort?: SortableCarouselField;
  sortBy?: 'asc' | 'desc';
  uniqKey?: string;
}) => {
  const uniqId = uniqKey || maybeUniqId;

  const {
    dispatch,
    options,
    currentHeading,
    direction,
    selectedFinanceOption,
    selectedFuels,
    isLoaded,
    selectedCategories,
    showPrivateMileages,
    businessEditionFilter,
    sortField,
    sortDirection,
  } = useStore(getStoreByKey(uniqId));
  const [sliderKey, setSliderKey] = useState(0);

  const setData = (models: ModelType[]) => {
    sessionStorage.setItem('kia-carousel-models', JSON.stringify(models));
    dispatch({ type: 'MODELS', payload: models });
    dispatch({ type: 'FILTERED_MODELS', payload: models });
    dispatch({
      type: 'SHOW_PRIVATE_MILEAGES',
      payload: true,
    });
    if (onlyBusinessEdition) {
      dispatch({ type: 'SET_BUSINESS_EDITION_FILTER', payload: 'only' });
    } else if (includeBusinessEdition) {
      dispatch({ type: 'SET_BUSINESS_EDITION_FILTER', payload: 'include' });
    } else {
      dispatch({ type: 'SET_BUSINESS_EDITION_FILTER', payload: 'exclude' });
    }
    if (pricelist) {
      dispatch({ type: 'SHOW_PRICELIST', payload: true });
    } else {
      if (heading) {
        dispatch({ type: 'HEADING', payload: heading });
      }
      if (imageType) {
        dispatch({ type: 'IMAGE_TYPE', payload: imageType });
      }
      if (fields) {
        dispatch({
          type: 'CONTROLS',
          payload: fields.split(',') as CarouselFieldsEnum[],
        });
      }
      if (fuelId) {
        dispatch({
          type: 'FUEL',
          payload: fuelId.split(',').map((f) => Number(f)),
        });
      }
      if (categoryIds) {
        dispatch({
          type: 'CATEGORY',
          payload: categoryIds.split(',').map((c) => Number(c)),
        });
      }
      if (sort) {
        dispatch({
          type: 'SORT_BY',
          payload: sort,
          sortBy,
        });
      }
    }
    dispatch({ type: 'IS_LOADED', payload: true });
  };

  const fetchModels = async () => {
    let models =
      JSON.parse(sessionStorage.getItem('kia-carousel-models')) ?? [];
    if (!models.length) {
      try {
        const { data } = await fetchWithType<ModelType[]>(
          getAdminUrl() + '/api/models',
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${getApiToken()}`,
            },
          },
        );
        if (data && data.length) {
          models = data;
        }
      } catch (error) {
        console.error('Error fetching trims:', error);
      }
    }
    setData(models);
  };

  useEffect(() => {
    if (isLoaded) {
      dispatch({ type: 'FILTER_MODELS' });
      setSliderKey((prev) => prev + 1);
    }
  }, [
    selectedFuels,
    selectedFinanceOption,
    selectedCategories,
    businessEditionFilter,
  ]);

  useEffect(() => {
    fetchModels();
  }, []);

  const getSnippetProps: () => string = () => {
    const businessEditionAttr =
      businessEditionFilter === 'only'
        ? ` onlyBusinessEdition="true"`
        : businessEditionFilter === 'include'
          ? ` includeBusinessEdition="true"`
          : '';
    if (!options) {
      return `pricelist="true" privateMileages="${showPrivateMileages}"${businessEditionAttr}`;
    }
    return (
      Object.entries(options)
        .map(([key, value]) => (value ? `${key}="${value}"` : ''))
        .filter(Boolean)
        .join(' ') +
      ` ${selectedFuels ? `fuelId="${selectedFuels.join(',')}"` : ''} ${selectedCategories ? `categoryIds="${selectedCategories.join(',')}"` : ''} ${sortField ? `sort="${sortField}" sortBy="${sortDirection}"` : ''} ${heading ? ` heading="${heading}"` : ''} privateMileages="${showPrivateMileages}"${businessEditionAttr}`
    );
  };

  return (
    <div
      className={`pt-4 pb-12 ${direction === 'vertical' ? 'max-w-screen-kia-sm mx-auto' : ''}`}
    >
      {!locationIsKia() && !hideControls ? (
        <>
          <CarouselControls uniqId={uniqId} type="carousel" />
          <Snippet page="carousel" props={getSnippetProps()} />
        </>
      ) : null}

      <div className="max-w-screen-kia-sm mx-auto relative">
        {currentHeading ? (
          <h1 className="pb-6 px-4">{currentHeading}</h1>
        ) : null}
        {!options ? (
          <CarouselFilters uniqId={uniqId} />
        ) : (
          businessEditionFilter !== 'only' && (
            <CarouselMileageFilter uniqId={uniqId} />
          )
        )}
        {!options ||
        options?.fields?.includes(CarouselFieldsEnum.CHANGE_DIRECTION) ? (
          <CarouselDirection uniqId={uniqId} />
        ) : null}
      </div>
      <CarouselSlider uniqId={uniqId} key={sliderKey} />
      {!options ||
      options?.fields?.includes(CarouselFieldsEnum.CTA_RETAILER) ? (
        <div
          className={`px-4 gap-4 pt-4 flex justify-center flex-col sm:flex-row ${direction === 'vertical' ? 'sm:hidden' : ''}`}
        >
          {!options ||
          options.fields.includes(CarouselFieldsEnum.CTA_RETAILER) ? (
            <a
              onClick={() =>
                trackEvent({
                  event: 'contact_dealer',
                  model: undefined,
                })
              }
              href="https://www.kia.com/se/provkor/#/"
            >
              <SecondaryButton>Kontakta återförsäljare</SecondaryButton>
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default Carousel;
